import { uploadCatalogAvatar } from './api'
import type { AvatarUploadResponse } from './types'

const cancelled = (): AvatarUploadResponse => ({status: 499, message: 'Загрузка остановлена.', errors: null})

const waitForRetry = (delay: number, signal: AbortSignal, onWait?: (seconds: number | null) => void): Promise<boolean> => new Promise(resolve => {
    const deadline = Date.now() + delay
    let timer: ReturnType<typeof setTimeout> | undefined
    let settled = false
    const finish = (completed: boolean) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        signal.removeEventListener('abort', abort)
        resolve(completed)
    }
    const abort = () => finish(false)
    const tick = () => {
        if (signal.aborted) {finish(false); return}
        const remaining = deadline - Date.now()
        if (remaining <= 0) {finish(true); return}
        onWait?.(Math.ceil(remaining / 1000))
        if (settled || signal.aborted) {finish(false); return}
        timer = setTimeout(tick, Math.min(1000, remaining))
    }
    signal.addEventListener('abort', abort, {once: true})
    tick()
})

/** A 429 is rejected before storage; retry only that explicit response. */
export const uploadCatalogAvatarWithRetry = async (
    file: File,
    folderID: number,
    onProgress: ((percent: number) => void) | undefined,
    signal: AbortSignal,
    onWait?: (seconds: number | null) => void,
): Promise<AvatarUploadResponse> => {
    try {
        while (!signal.aborted) {
            let response: AvatarUploadResponse
            try { response = await uploadCatalogAvatar(file, folderID, onProgress, signal) }
            catch (error) { if (signal.aborted) return cancelled(); throw error }
            if (signal.aborted) return cancelled()
            if (response.status !== 429) return response
            const delay = Number.isFinite(response.retryAfterMs) && response.retryAfterMs! >= 0
                ? Math.max(1000, response.retryAfterMs!) : 60000
            const completed = await waitForRetry(delay, signal, onWait)
            onWait?.(null)
            if (!completed || signal.aborted) return cancelled()
        }
        return cancelled()
    } finally { onWait?.(null) }
}
